import type { InsightContent } from "./types";

export const keywordsNotInternational: InsightContent = {
  id: "keywords-not-international",
  format: "guide",
  cluster: "International SEO",
  moneyPage: {
    es: "/servicios/seo-internacional",
    en: "/en/services/international-seo",
  },
  path: {
    es: "/biblioteca/traducir-keywords-no-es-seo-internacional",
    en: "/en/library/translating-keywords-is-not-international-seo",
  },
  published: "2026-08-11",
  updated: "2026-08-11",
  readingMinutes: 9,
  es: {
    title: "Traducir palabras clave no es SEO internacional",
    h1: "Traducir palabras clave no es SEO internacional",
    description:
      "Traducir tu lista de keywords no equivale a una estrategia internacional. Intención, SERP y arquitectura cambian por mercado, aunque el idioma sea el mismo.",
    eyebrow: "SEO Internacional",
    summary:
      "Traducir una lista de palabras clave del inglés a otro idioma ignora que la intención de búsqueda, la composición del SERP y la arquitectura técnica cambian por mercado. Esta guía explica qué revisar antes de expandirte.",
    body: [
      {
        type: "p",
        text: "Es un patrón habitual: una empresa exporta su lista de keywords en inglés, la pasa por un traductor (humano o automático) y la usa como base para el contenido en otro idioma. El problema no es la traducción en sí, sino asumir que resuelve la estrategia. No la resuelve.",
      },
      {
        type: "h2",
        text: "La intención de búsqueda no viaja con la traducción",
      },
      {
        type: "p",
        text: "Un mismo concepto puede buscarse con términos distintos, en momentos distintos del proceso de compra, o con expectativas distintas de resultado según el país. Dos mercados que comparten idioma —como México y España, o España y el mercado hispanohablante de Estados Unidos— no comparten necesariamente el mismo vocabulario, la misma fase de decisión ni los mismos competidores para el mismo término.",
      },
      {
        type: "callout",
        text: "Traducir una keyword da como resultado una palabra correcta en otro idioma. No da como resultado una palabra que la gente de ese mercado realmente use para buscar ese tema.",
      },
      {
        type: "h2",
        text: "El SERP también cambia de mercado a mercado",
      },
      {
        type: "list",
        items: [
          "Los competidores que dominan un resultado en un país rara vez son los mismos en otro, aunque el idioma coincida.",
          "Las funciones del SERP (fragmentos destacados, paneles de conocimiento, resultados locales) pueden estar presentes en un mercado y ausentes en otro para la misma consulta.",
          "El nivel de competencia por un mismo término puede ser mucho menor en un mercado que en otro, cambiando la prioridad de qué contenido crear primero.",
        ],
      },
      {
        type: "h2",
        text: "Arquitectura: ccTLD, subdominio o subcarpeta",
      },
      {
        type: "p",
        text: "Antes de escribir una sola palabra localizada hay una decisión estructural que condiciona todo lo demás: cómo vas a organizar las versiones de tu sitio por país o idioma.",
      },
      {
        type: "table",
        head: ["Estructura", "Cuándo tiene sentido", "Costo de mantenimiento"],
        rows: [
          [
            "ccTLD (.mx, .es, .com.co)",
            "Señal geográfica más fuerte; útil si el mercado es prioritario a largo plazo y hay recursos para sostener cada dominio",
            "Alto: cada dominio construye autoridad por separado",
          ],
          [
            "Subdominio (mx.sitio.com)",
            "Permite cierta separación técnica sin comprar dominios nuevos",
            "Medio: en general se trata como entidad semi-independiente en autoridad",
          ],
          [
            "Subcarpeta (sitio.com/mx/)",
            "Aprovecha la autoridad de dominio existente; recomendable cuando el equipo es pequeño o el mercado aún se está validando",
            "Bajo: un solo dominio que concentra autoridad",
          ],
        ],
      },
      {
        type: "p",
        text: "No existe una respuesta universal. La decisión depende de recursos disponibles, del dominio actual y de si el objetivo de marca por país exige separación total.",
      },
      {
        type: "h2",
        text: "Hreflang: reciprocidad y x-default",
      },
      {
        type: "p",
        text: "El hreflang le indica a los motores de búsqueda qué versión de una página mostrar según idioma y región del usuario. Su implementación tiene dos requisitos que suelen fallar en la práctica: debe ser recíproco (si la página A apunta a B, B debe apuntar de vuelta a A) y conviene declarar un x-default para usuarios que no calzan con ninguna variante específica.",
      },
      {
        type: "quote",
        text: "Cada página debe identificarse a sí misma con un elemento hreflang, además de identificar a las demás versiones de idioma/región.",
        source: {
          label: "Google Search Central — Localized versions",
          href: "https://developers.google.com/search/docs/specialty/international/localized-versions",
        },
      },
      {
        type: "list",
        items: [
          "Falta de reciprocidad: la página en inglés apunta a la versión en español, pero la versión en español no apunta de vuelta.",
          "Uso de códigos de idioma sin región cuando en realidad hay variantes por país (es vs. es-MX vs. es-US).",
          "Ausencia de x-default, dejando a Google sin una versión de respaldo clara para tráfico que no coincide con ningún hreflang declarado.",
          "Hreflang apuntando a URLs canonicalizadas hacia otra página, lo que anula la señal.",
        ],
      },
      {
        type: "h2",
        text: "Checklist antes de lanzar una versión internacional",
      },
      {
        type: "steps",
        items: [
          "Validar que existe volumen de búsqueda real en el mercado destino, no solo asumirlo por tamaño de población.",
          "Investigar palabras clave de forma independiente en el mercado destino, sin partir de la lista traducida como base final.",
          "Revisar el SERP real de ese mercado para las consultas prioritarias: quién compite, qué formato de contenido gana.",
          "Elegir arquitectura (ccTLD, subdominio o subcarpeta) según recursos y objetivo de marca, no por defecto.",
          "Implementar hreflang recíproco entre todas las versiones relevantes, incluyendo x-default.",
          "Adaptar contenido más allá del idioma: moneda, unidades, referencias culturales y ejemplos locales.",
          "Configurar medición segmentada por mercado antes de lanzar, para poder evaluar resultados por separado.",
        ],
      },
      {
        type: "h2",
        text: "Qué hacer con lo aprendido",
      },
      {
        type: "p",
        text: "Traducir es un paso mecánico dentro de una expansión internacional, no la estrategia completa. La investigación de intención, la arquitectura y el hreflang son lo que determina si esa traducción llega a la persona correcta en el resultado correcto.",
      },
    ],
    cta: {
      text: "Si estás evaluando expandirte a otro país o idioma, vale la pena validar la estrategia antes de invertir en contenido.",
      label: "Ver SEO Internacional",
      href: "/servicios/seo-internacional",
    },
    related: [
      {
        label: "SEO para el mercado hispano de Estados Unidos",
        href: "/biblioteca/seo-mercado-hispano-estados-unidos",
      },
      { label: "SEO Internacional", href: "/servicios/seo-internacional" },
      {
        label: "SEO bilingüe para el mercado hispano",
        href: "/servicios/seo-bilingue-mercado-hispano",
      },
    ],
  },
  en: {
    title: "Translating Keywords Is Not International SEO",
    h1: "Translating Keywords Is Not International SEO",
    description:
      "Translating your keyword list isn't an international strategy. Intent, SERP composition and architecture shift by market even when the language is the same.",
    eyebrow: "International SEO",
    summary:
      "Translating a keyword list from English into another language ignores that search intent, SERP composition, and technical architecture all shift by market. This guide covers what to check before expanding.",
    body: [
      {
        type: "p",
        text: "It's a common pattern: a company exports its English keyword list, runs it through a translator — human or automated — and uses that as the basis for content in another language. The problem isn't translation itself. It's assuming translation solves the strategy. It doesn't.",
      },
      {
        type: "h2",
        text: "Search intent doesn't travel with the translation",
      },
      {
        type: "p",
        text: "The same concept can be searched with different terms, at different stages of the buying process, or with different result expectations depending on the country. Two markets that share a language — Mexico and Spain, or Spain and the US Hispanic market — don't necessarily share the same vocabulary, the same decision stage, or the same competitors for the same term.",
      },
      {
        type: "callout",
        text: "Translating a keyword produces a correct word in another language. It doesn't produce the word that people in that market actually use to search for that topic.",
      },
      {
        type: "h2",
        text: "The SERP changes from market to market too",
      },
      {
        type: "list",
        items: [
          "The competitors dominating a result in one country are rarely the same in another, even when the language matches.",
          "SERP features (featured snippets, knowledge panels, local results) can appear for a query in one market and be absent for the same query in another.",
          "Competition level for the same term can be significantly lower in one market than another, changing which content should be prioritized first.",
        ],
      },
      {
        type: "h2",
        text: "Architecture: ccTLD, subdomain or subfolder",
      },
      {
        type: "p",
        text: "Before writing a single localized word, there's a structural decision that shapes everything else: how you're going to organize your site's versions by country or language.",
      },
      {
        type: "table",
        head: ["Structure", "When it makes sense", "Maintenance cost"],
        rows: [
          [
            "ccTLD (.mx, .es, .com.co)",
            "Strongest geo signal; useful when the market is a long-term priority and there are resources to sustain each domain",
            "High: each domain builds authority separately",
          ],
          [
            "Subdomain (mx.site.com)",
            "Allows some technical separation without buying new domains",
            "Medium: often treated as a semi-independent entity for authority",
          ],
          [
            "Subfolder (site.com/mx/)",
            "Leverages existing domain authority; often the right call for small teams or markets still being validated",
            "Low: a single domain concentrates authority",
          ],
        ],
      },
      {
        type: "p",
        text: "There's no universal answer. The decision depends on available resources, the current domain, and whether brand strategy per country requires full separation.",
      },
      {
        type: "h2",
        text: "Hreflang: reciprocity and x-default",
      },
      {
        type: "p",
        text: "Hreflang tells search engines which page version to show based on a user's language and region. Correct implementation has two requirements that commonly fail in practice: it must be reciprocal (if page A points to B, B must point back to A), and it should include an x-default for users who don't match any specific variant.",
      },
      {
        type: "quote",
        text: "Each page should identify itself with a hreflang element, in addition to identifying the other language/region versions.",
        source: {
          label: "Google Search Central — Localized versions",
          href: "https://developers.google.com/search/docs/specialty/international/localized-versions",
        },
      },
      {
        type: "list",
        items: [
          "Missing reciprocity: the English page points to the Spanish version, but the Spanish version doesn't point back.",
          "Using language codes without region when country variants actually exist (es vs. es-MX vs. es-US).",
          "No x-default, leaving Google without a clear fallback version for traffic that doesn't match any declared hreflang.",
          "Hreflang pointing to URLs that canonicalize to a different page, which cancels the signal.",
        ],
      },
      {
        type: "h2",
        text: "Checklist before launching an international version",
      },
      {
        type: "steps",
        items: [
          "Validate that real search volume exists in the target market — don't assume it based on population size alone.",
          "Research keywords independently in the target market, without starting from the translated list as a final basis.",
          "Review the actual SERP in that market for priority queries: who's competing, what content format is winning.",
          "Choose architecture (ccTLD, subdomain, or subfolder) based on resources and brand objectives, not by default.",
          "Implement reciprocal hreflang across all relevant versions, including x-default.",
          "Adapt content beyond language: currency, units, cultural references, and local examples.",
          "Set up market-segmented measurement before launch so results can be evaluated separately.",
        ],
      },
      {
        type: "h2",
        text: "What to do with this",
      },
      {
        type: "p",
        text: "Translation is a mechanical step within an international expansion, not the whole strategy. Intent research, architecture, and hreflang are what determine whether that translation actually reaches the right person in the right result.",
      },
    ],
    cta: {
      text: "If you're evaluating expansion into another country or language, it's worth validating the strategy before investing in content.",
      label: "See International SEO",
      href: "/en/services/international-seo",
    },
    related: [
      {
        label: "SEO for the US Hispanic Market",
        href: "/en/library/seo-us-hispanic-market",
      },
      { label: "International SEO", href: "/en/services/international-seo" },
      {
        label: "Hispanic Market SEO",
        href: "/en/services/hispanic-market-seo",
      },
    ],
  },
};
